import { BigNumber, Signer, utils } from 'ethers';
import LabelValue from '../../components/LabelValue/LabelValue';
import { ToasterWithStatusAndDesriptionParams } from '../../context-providers/ToasterProvider/ToasterProvider';
import { contractAddresses } from '../../helpers/config/contracts.config';
import { getErrorMessage } from '../../helpers/error-message.helper';
import { formatCurrencyAmount } from '../../helpers/formatters';
import { calculatePositionSizeWithPosition } from '../../helpers/math/position-math.helpers';
import { signatureHelper } from '../../helpers/signature.helper';
import Singleton from '../../helpers/singleton';
import { IOpenPositionBody, IPositionResponse, IUpdatePositionMessage } from '../api/api.interfaces';
import apiService, { PositionStatusFilter } from '../api/api.service';
import { positionsStore } from './position.store';
import { capitalize } from 'lodash';

interface PositionsManagerDependencies {
    userAddress: `0x${string}`;
    signer: Signer;
    addToasterWithStatusAndDescription: (params: ToasterWithStatusAndDesriptionParams) => void;
    closeModal: () => void;
    signTypedData: (data: any) => Promise<string>;
}


@Singleton
export class PositionsManager {

    private userAddress!: string;
    private signTypedData!: (data: any) => Promise<string>;
    private showToaster!: (params: ToasterWithStatusAndDesriptionParams) => void;
    private closeModal!: () => void;

    public injectDependencies(deps: PositionsManagerDependencies) {
        this.userAddress = deps.userAddress;
        this.signTypedData = deps.signTypedData;
        this.showToaster = deps.addToasterWithStatusAndDescription;
        this.closeModal = deps.closeModal;
    }

    async fetchOpenPositions(userAddress: string) {
        try {
            const positions = await apiService.positionsGet(userAddress, PositionStatusFilter.OPEN);
            positionsStore.setOpenPositions(positions);
        } catch (error) {
            console.error('Failed to load open positions:', error);
        }
    }

    async fetchClosedPositions(userAddress: string) {
        if (!userAddress) {
            return;
        }
        try {
            const positions = await apiService.positionsGet(userAddress, PositionStatusFilter.CLOSED);
            positionsStore.setClosedPositions(positions);
        } catch (error) {
            console.error('Failed to load closed positions:', error);
        }
    }

    async signAndOpenPosition(amountBN: BigNumber, symbol: string, leverage: number, positionSize: number, currentPrice: number, tpDecimal?: number | null, slDecimal?: number | null) {

        const normalizedTp = tpDecimal ? utils.parseUnits(tpDecimal + '', 8).toString() : '0';
        const normalizedSl = slDecimal ? utils.parseUnits(slDecimal + '', 8).toString() : '0';

        const toasterId = this.getToasterId({
            amount: amountBN.toString(),
            symbol,
            leverage,
            tp: normalizedTp,
            sl: normalizedSl,
            action: 'openPosition'
        });

        const [currency, , longOrShort] = symbol.split(':');

        const message = {
            user: this.userAddress,
            symbol,
            token: contractAddresses.polygon.usdt,
            amount: amountBN.toString(),
            leverage,
            nonce: Date.now(),
            deadline: signatureHelper.getDeadlineIn7Days()
        };

        const toasterTitle = `Open Position - ${currency.toUpperCase()} ${capitalize(longOrShort)}`;

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: toasterTitle,
            description: this.makePositionDescription(currentPrice, positionSize, currency, tpDecimal, slDecimal)
        });

        try {
            const signature = await signatureHelper.signOpenPositionMessage(this.signTypedData, message);
            this.closeModal();
            const position = await this.openPosition({
                message,
                signature,
                tp: normalizedTp,
                sl: normalizedSl
            } as any);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: toasterTitle,
                description: this.makePositionDescription(currentPrice, positionSize, currency, tpDecimal, slDecimal),
                closeTime: 10000
            });
            return position
        } catch (err) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: toasterTitle,
                description: getErrorMessage(err),
                closeTime: 15000
            })
            throw err;
        }
    }

    async openPosition(data: IOpenPositionBody) {
        try {
            const newPosition = await apiService.positionsPost(data);
            positionsStore.addPosition(newPosition);
            return newPosition;
        } catch (error) {
            console.error('Failed to open position:', error);
            throw error;
        }
    }

    async updatePosition(message: Omit<IUpdatePositionMessage, 'nonce' | 'deadline'>) {

        const toasterId = Date.now().toString()
        const position: IPositionResponse = positionsStore.openPositions[message.positionId]
        const [currency, , longOrShort] = position.symbol.split(':');
        const toasterTitle = `Update Position - ${currency.toUpperCase()} ${capitalize(longOrShort)}`;

        const tpDecimal = Number(message.tp) ? Number(utils.formatUnits(message.tp, 8)) : null
        const slDecimal = Number(message.sl) ? Number(utils.formatUnits(message.sl, 8)) : null

        const description = this.makePositionDescription(Number(position.initialPrice), calculatePositionSizeWithPosition(position), currency, tpDecimal, slDecimal);

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: toasterTitle,
            description
        });
        try {
            const requestParams = await this.createRequestParamsForPositionUpdate(message);
            const updatedPosition = await apiService.positionsUpdatePost(requestParams);
            positionsStore.updatePosition(updatedPosition);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: toasterTitle,
                description,
                closeTime: 10000
            });
            return updatedPosition;
        } catch (err) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: toasterTitle,
                description: getErrorMessage(err),
                closeTime: 15000
            })
            throw err;
        }
    }

    async closePosition(positionId: number) {

        const toasterId = Date.now().toString()
        const position: IPositionResponse = positionsStore.openPositions[positionId]
        const [currency, , longOrShort] = position.symbol.split(':');
        const toasterTitle = `Close Position - ${currency.toUpperCase()} ${capitalize(longOrShort)}`;

        const description = this.makePositionDescription(Number(position.initialPrice), calculatePositionSizeWithPosition(position), currency, Number(position.tpPrice), Number(position.slPrice));

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: toasterTitle,
            description
        });
        try {
            const requestParams = await this.createRequestParamsForPositionClose(positionId);
            const closedPosition = await apiService.positionsClosePost(requestParams);
            positionsStore.removePosition(closedPosition.id);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: toasterTitle,
                description,
                closeTime: 10000
            });
            return closedPosition;
        } catch (err) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: toasterTitle,
                description: getErrorMessage(err),
                closeTime: 15000
            })
            throw err;
        }
    }

    private async createRequestParamsForPositionUpdate(message: Omit<IUpdatePositionMessage, 'nonce' | 'deadline'>) {
        const nonce = Date.now();
        const deadline = signatureHelper.getDeadlineIn7Days();
        const extendedMessage: IUpdatePositionMessage = {
            ...message,
            nonce,
            deadline
        };
        const signature = await signatureHelper.signUpdatePositionMessage(this.signTypedData, extendedMessage);
        return {
            message: extendedMessage,
            signature
        };
    }

    private async createRequestParamsForPositionClose(positionId: number) {
        const nonce = Date.now();
        const deadline = signatureHelper.getDeadlineIn7Days();
        const message = {
            user: this.userAddress,
            positionId,
            nonce,
            deadline
        };
        const signature = await signatureHelper.signClosePositionMessage(this.signTypedData, message);
        return {
            message: {
                user: this.userAddress,
                positionId,
                nonce,
                deadline
            },
            signature
        };
    }

    private getToasterId(obj: any) {
        return JSON.stringify({
            ...obj,
            timestamp: Date.now()
        });
    }

    private makePositionDescription(currentPrice: number, positionSize: number, currency: string, tp?: number | null, sl?: number | null) {

        let tpSlLabel = '';
        let tpSlValue = '';

        let slValue = sl ? formatCurrencyAmount(sl, '$') : '';
        let tpValue = tp ? formatCurrencyAmount(tp, '$') : '';

        if (tp && sl) {
            tpSlLabel = 'TP/SL Prices';
            tpSlValue = `${tpValue}/${slValue}`
        } else if (tp) {
            tpSlLabel = 'TP Price';
            tpSlValue = tpValue;
        } else if (sl) {
            tpSlLabel = 'SL Price';
            tpSlValue = slValue
        }

        return (<>
            <LabelValue label="Price" value={formatCurrencyAmount(currentPrice, '$')}/>
            {tpSlLabel && <LabelValue label={tpSlLabel} value={tpSlValue}/>}
            <LabelValue label="Size" value={`${positionSize.toFixed(4)} ${currency.toUpperCase()}`}/>
        </>);
    }

}
